import { FC, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { parseContent } from '../utils'
import { markdownStyles, UserMessageText } from '../chatArea.styles'
import { parseMarkdownTable } from '../../../utils/parseMarkdownTable'
import { formatHeaderLabel } from '../../../utils/formatHeader'
import D3Chart from './D3Chart'
import MarkdownErrorBoundary from './MarkdownErrorBoundary'
import NexaTable from './NexaTable'

interface MessageContentProps {
  content: string
  isUser?: boolean
}

const MessageContent: FC<MessageContentProps> = ({ content, isUser = false }) => {
  const parts = useMemo(() => parseContent(content), [content])

  // For user messages, render simple styled text
  if (isUser) {
    return <UserMessageText>{content}</UserMessageText>
  }

  return (
    <>
      {parts.map((part, index) => {
        if (part.type === 'chart' && part.chartConfig) {
          return <D3Chart key={index} config={part.chartConfig} />
        }
        return (
          <MarkdownErrorBoundary key={index} fallbackContent={part.content}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 style={markdownStyles.h1}>{children}</h1>,
                h2: ({ children }) => <h2 style={markdownStyles.h2}>{children}</h2>,
                h3: ({ children }) => <h3 style={markdownStyles.h3}>{children}</h3>,
                p: ({ children }) => <p style={markdownStyles.p}>{children}</p>,
                strong: ({ children }) => <strong style={markdownStyles.strong}>{children}</strong>,
                ul: ({ children }) => <ul style={markdownStyles.ul}>{children}</ul>,
                ol: ({ children }) => <ol style={markdownStyles.ol}>{children}</ol>,
                li: ({ children }) => <li style={markdownStyles.li}>{children}</li>,
                blockquote: ({ children }) => <blockquote style={markdownStyles.blockquote}>{children}</blockquote>,
                table: ({ children }) => {
                  const { headers, rows } = parseMarkdownTable(children)
                  if (!headers.length) return null
                  return <NexaTable headers={headers.map(formatHeaderLabel)} rows={rows} />
                },
                code: ({ children, className }) => {
                  const isInline = !className
                  if (isInline) {
                    return <code style={markdownStyles.inlineCode}>{children}</code>
                  }
                  return (
                    <pre style={markdownStyles.codeBlock}>
                      <code style={markdownStyles.code}>{children}</code>
                    </pre>
                  )
                }
              }}
            >
              {part.content}
            </ReactMarkdown>
          </MarkdownErrorBoundary>
        )
      })}
    </>
  )
}

export default MessageContent
